import { Decoration, WidgetType } from "./decoration";
import { ViewPlugin } from "./extension";
import { EditorView } from "./editorview";
import { combineConfig, Facet } from "@codemirror/next/state";
import { countColumn, codePointAt } from "@codemirror/next/text";
import { StyleModule } from "style-mod";
const Specials = /[\u0000-\u0008\u000a-\u001f\u007f-\u009f\u00ad\u061c\u200b-\u200c\u200e\u200f\u2028\u2029\ufeff\ufff9-\ufffc]/gu;
const Names = {
    0: "null",
    7: "bell",
    8: "backspace",
    10: "newline",
    11: "vertical tab",
    13: "carriage return",
    27: "escape",
    8203: "zero width space",
    8204: "zero width non-joiner",
    8205: "zero width joiner",
    8206: "left-to-right mark",
    8207: "right-to-left mark",
    8232: "line separator",
    8233: "paragraph separator",
    65279: "zero width no-break space",
    65532: "object replacement"
};
let _supportsTabSize = null;
function supportsTabSize() {
    if (_supportsTabSize == null && typeof document != "undefined" && document.body) {
        let styles = document.body.style;
        _supportsTabSize = (styles.tabSize || styles.MozTabSize) != null;
    }
    return _supportsTabSize || false;
}
const UnicodeRegexpSupport = /x/.unicode != null ? "gu" : "g";
const specialCharConfig = Facet.define({
    combine(configs) {
        let config = combineConfig(configs, {
            render: null,
            specialChars: Specials,
            addSpecialChars: null
        });
        if (config.replaceTabs = !supportsTabSize())
            config.specialChars = new RegExp("\t|" + config.specialChars.source, UnicodeRegexpSupport);
        if (config.addSpecialChars)
            config.specialChars = new RegExp(config.specialChars.source + "|" + config.addSpecialChars.source, UnicodeRegexpSupport);
        return config;
    }
});
/// Returns an extension that installs highlighting of special
/// characters.
export function highlightSpecialChars(
/// Configuration options.
config = {}) {
    let ext = [specialCharConfig.of(config), specialCharPlugin];
    if (!supportsTabSize())
        ext.push(tabStyle);
    return ext;
}
const specialCharPlugin = ViewPlugin.fromClass(class {
    constructor(view) {
        this.view = view;
        this.decorations = Decoration.none;
        this.decorationCache = Object.create(null);
        this.recompute();
    }
    update(update) {
        let confChange = update.startState.facet(specialCharConfig) != update.state.facet(specialCharConfig);
        if (confChange)
            this.decorationCache = Object.create(null);
        if (confChange || update.changes.length || update.viewportChanged)
            this.recompute();
    }
    recompute() {
        let decorations = [];
        for (let { from, to } of this.view.visibleRanges)
            this.getDecorationsFor(from, to, decorations);
        this.decorations = Decoration.set(decorations);
    }
    getDecorationsFor(from, to, target) {
        let config = this.view.state.facet(specialCharConfig);
        let { doc } = this.view.state;
        for (let pos = from, cursor = doc.iterRange(from, to), m; !cursor.next().done;) {
            if (!cursor.lineBreak) {
                while (m = config.specialChars.exec(cursor.value)) {
                    let code = codePointAt(m[0], 0), deco;
                    if (code == null)
                        continue;
                    if (code == 9) {
                        let line = doc.lineAt(pos + m.index);
                        let size = this.view.state.tabSize, col = countColumn(doc.sliceString(line.from, pos + m.index), 0, size);
                        deco = Decoration.replace({ widget: new TabWidget((size - (col % size)) * this.view.defaultCharacterWidth) });
                    }
                    else {
                        deco = this.decorationCache[code] ||
                            (this.decorationCache[code] = Decoration.replace({ widget: new SpecialCharWidget(config, code) }));
                    }
                    target.push(deco.range(pos + m.index, pos + m.index + m[0].length));
                }
            }
            pos += cursor.value.length;
        }
    }
}, {
    decorations: v => v.decorations
});
const DefaultPlaceholder = "\u2022";
// Assigns placeholder characters from the Control Pictures block to
// ASCII control characters
function placeholder(code) {
    if (code >= 32)
        return DefaultPlaceholder;
    if (code == 10)
        return "\u2424";
    return String.fromCharCode(9216 + code);
}
class SpecialCharWidget extends WidgetType {
    constructor(options, code) {
        super();
        this.options = options;
        this.code = code;
    }
    eq(other) { return other.code == this.code; }
    toDOM(view) {
        let ph = placeholder(this.code);
        let desc = view.state.phrase("Control character ") + (Names[this.code] || "0x" + this.code.toString(16));
        let custom = this.options.render && this.options.render(this.code, desc, ph);
        if (custom)
            return custom;
        let span = document.createElement("span");
        span.textContent = ph;
        span.title = desc;
        span.setAttribute("aria-label", desc);
        span.style.color = "red";
        return span;
    }
    ignoreEvent() { return false; }
}
class TabWidget extends WidgetType {
    constructor(width) {
        super();
        this.width = width;
    }
    eq(other) { return other.width == this.width; }
    toDOM() {
        let span = document.createElement("span");
        span.textContent = "\t";
        span.className = tab;
        span.style.width = this.width + "px";
        return span;
    }
    ignoreEvent() { return false; }
}
const tab = StyleModule.newName(), tabStyle = EditorView.styleModule.of(new StyleModule({
    ["." + tab]: {
        display: "inline-block",
        overflow: "hidden",
        verticalAlign: "bottom"
    }
}));
